"use client";

import { useLocale } from "@i18n/react";
import { useQueryClient } from "@tanstack/react-query";

import { CommunityType } from "../../../../lib/types/enums/community/communityType";
import type { CommunityUpdateDetails } from "../../../../lib/types/schema/community/admin/communityUpdateDetailsSchema";
import { communitySchema } from "../../../../lib/types/schema/community/communitySchema";
import { useRequestMutation } from "../../../../request/hooks/useRequestMutation";
import { keyCommunitiesChildren } from "../children/keyCommunitiesChildren";
import { keyCommunityEventsByCommunity } from "../events/keyCommunityEvent";
import { keyCommunitiesAdminCommunities } from "./keyCommunitiesAdminCommunities";
import { keyCommunitiesAdminCommunity } from "./keyCommunitiesAdminCommunity";

export const useRequestMutationsCommunitiesAdminCommunity = ({
  id,
}: {
  id: number;
}) => {
  const locale = useLocale();
  const queryClient = useQueryClient();

  return {
    mutationJoin: useRequestMutation({
      mutationFnRequestProps: {
        method: "POST",
        schema: communitySchema,
        urlBackendPath: `/me/communities/${id}/members`,
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [keyCommunitiesAdminCommunity({ id }), locale],
        });
        queryClient.invalidateQueries({
          queryKey: keyCommunityEventsByCommunity(id),
        });
      },
    }),
    mutationPut: useRequestMutation({
      mutationFnRequestProps: (data: CommunityUpdateDetails) => ({
        body: JSON.stringify(data),
        method: "PUT",
        schema: communitySchema,
        urlBackendPath: `/me/communities/${id}`,
      }),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [keyCommunitiesAdminCommunity({ id }), locale],
        });
        queryClient.invalidateQueries({
          queryKey: [keyCommunitiesAdminCommunities(), locale],
        });
      },
    }),
    mutationUnlink: useRequestMutation({
      mutationFnRequestProps: (data: { id: number }) => ({
        body: JSON.stringify(data),
        method: "PUT",
        urlBackendPath: `/communities/${data.id}/unlink`,
      }),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [
            keyCommunitiesChildren({ id, type: CommunityType.basic }),
            locale,
          ],
        });
        queryClient.invalidateQueries({
          queryKey: [
            keyCommunitiesChildren({ id, type: CommunityType.parish }),
            locale,
          ],
        });
        queryClient.invalidateQueries({
          queryKey: [
            keyCommunitiesChildren({ id, type: CommunityType.small_group }),
            locale,
          ],
        });
      },
    }),
  };
};
